import * as React from 'react';

const QUERY = '(prefers-reduced-motion: reduce)';

function subscribe(onChange: () => void) {
  const mql = window.matchMedia(QUERY);
  mql.addEventListener('change', onChange);
  return () => mql.removeEventListener('change', onChange);
}

/**
 * Whether the visitor has asked their OS to cut down on animation.
 *
 * Most motion in the product is a CSS transition, and those are better handled
 * by the `motion-reduce:` variant than by a hook. This exists for the cases
 * where the decision is structural rather than cosmetic — where the answer is
 * not "animate less" but "do not load this at all". A hero's background video
 * is the one that matters: for someone with a vestibular disorder, a looping
 * pan behind the headline is the thing that makes the page unusable, and no
 * amount of shortening the transition helps.
 *
 * Reads live, so the video stops if the preference is changed while the page
 * is open rather than only on the next visit.
 */
export function useReducedMotion() {
  return React.useSyncExternalStore(
    subscribe,
    () => window.matchMedia(QUERY).matches,
    // No server here — this is a Vite SPA — but the fallback has to pick a
    // side, and the side that errs toward not autoplaying is the safe one.
    () => true,
  );
}
